import { DEMO_ACCOUNTS, DEMO_GUEST, type DemoMember, type DemoPresence, type DemoRequest } from '../demoBots';
import { OWN_LOOK, plainLook } from '../participantColors';
import type { ParticipantLook } from '../presenceSource';
import { t } from '../../i18n/I18n';
import { MIN_SEARCH, type LinkAccess, type ShareInvitee, type ShareMatch, type SharePerson, type ShareSource, type ShareState } from './source';

const refused = (code: string) => Object.assign(new Error(code), { code });
// Kept once: `get` must answer the same state while nothing changed, bots or none.
const NOBODY: readonly DemoMember[] = [], NO_REQUESTS: readonly DemoRequest[] = [];

/**
 * The Share dialog in a project of this browser: you as the owner and the demo bots as members, or you alone when they are off.
 * `guest`: the same room seen as its guest `DEMO_GUEST` would, whose requests reach you back as the owner.
 */
export class DemoShare implements ShareSource {
  private seen: { members: readonly DemoMember[]; requests: readonly DemoRequest[] } | null = null;
  private state: ShareState | null = null;
  /** Without the bots only; with them the room keeps it (`DemoPresence.link`), so the guest view and back find it as it was. */
  private link: LinkAccess = 'invited';
  private readonly listeners = new Set<() => void>();

  constructor(private readonly demo: DemoPresence | null, private readonly view: 'owner' | 'guest' = 'owner') {}

  get = (): ShareState => {
    const members = this.demo?.members() ?? NOBODY, requests = this.demo?.requests() ?? NO_REQUESTS;
    if (this.state && this.seen?.members === members && this.seen.requests === requests) return this.state;
    this.seen = { members, requests };
    const guest = this.view === 'guest', me = guest ? members.find(member => member.id === DEMO_GUEST) : null;
    const owner: SharePerson = { id: guest ? 'demo:owner' : 'self', name: t(guest ? '#WebShareDemoOwner' : '#ShareYou'), role: 'owner', look: OWN_LOOK,
      self: !guest, pending: false, online: true, visitor: false, manageable: false };
    const people = [owner, ...members.map((member): SharePerson => ({
      id: member.id, name: member.name, ...(member.email ? { email: member.email } : {}), role: member.role, look: lookOf(member),
      self: member === me, pending: !member.online && member !== me, online: member.online || member === me, visitor: false, manageable: !guest,
    }))];
    const myRole = guest ? me?.role ?? 'viewer' : 'owner';
    this.state = { status: 'ready', error: null, mode: this.demo ? 'demo' : 'local', myRole, people, link: this.demo?.link ?? this.link, noLink: 'local',
      requests: guest ? [] : requests.map(request => {
        const member = members.find(candidate => candidate.id === request.id);
        return { id: request.id, name: request.name, wants: request.wants, look: member ? lookOf(member) : plainLook(request.id, 'viewer') };
      }),
      editRequest: guest && myRole === 'viewer' ? requests.some(request => request.id === DEMO_GUEST) ? 'sent' : 'idle' : null };
    return this.state;
  };

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    const stop = this.demo?.subscribeMembers(listener);
    return () => { this.listeners.delete(listener); stop?.(); };
  };

  async refresh(): Promise<void> {}

  async load(): Promise<void> {}

  /** Nobody else can open a project kept in this browser, so there is no address to give. */
  async linkUrl(): Promise<string> { throw refused('forbidden'); }

  async setLink(link: LinkAccess): Promise<void> {
    if (this.demo) this.demo.link = link; else this.link = link;
    this.state = null;
    for (const listener of this.listeners) listener();
  }

  async setRole(id: string, role: 'editor' | 'viewer'): Promise<void> { this.demo?.setRole(id, role); }

  async remove(id: string): Promise<void> { this.demo?.remove(id); }

  async search(term: string): Promise<ShareMatch[]> {
    const needle = term.trim().toLowerCase();
    if (needle.length < MIN_SEARCH) return [];
    return DEMO_ACCOUNTS.filter(account => account.name.toLowerCase().includes(needle) || account.email.startsWith(needle))
      .map(account => ({ id: `guest:${account.email}`, name: account.name }));
  }

  /** A demo invitee's id is `guest:<address>` (`DemoPresence.invite`), so a match names the member it becomes. */
  async invite(invitee: ShareInvitee, role: 'editor' | 'viewer'): Promise<void> {
    const email = 'email' in invitee ? invitee.email : invitee.id.replace(/^guest:/, '');
    if (!this.demo?.invite(email, role)) throw refused('conflict');
  }

  async approve(id: string): Promise<void> { if (this.view !== 'owner' || !this.demo?.approve(id)) throw refused('gone'); }

  async deny(id: string): Promise<void> { if (this.view !== 'owner' || !this.demo?.deny(id)) throw refused('gone'); }

  async requestEdit(): Promise<void> {
    const me = this.view === 'guest' ? this.demo?.members().find(member => member.id === DEMO_GUEST) : null;
    if (!me || !this.demo?.ask({ id: me.id, name: me.name, wants: 'editor' })) throw refused('conflict');
  }

  async leave(): Promise<void> {
    if (this.view !== 'guest' || !this.demo?.members().some(member => member.id === DEMO_GUEST)) throw refused('forbidden');
    this.demo.remove(DEMO_GUEST);
  }

  dispose(): void {}
}

function lookOf(member: DemoMember): ParticipantLook {
  return { color: member.color, ink: member.ink, agent: member.agent, role: member.role, icon: member.icon, avatar: member.avatar, halo: member.halo, owner: member.owner };
}
